import React from 'react';
/**
 * ScratchpadDrawer — Slide-in panel that wraps ScratchpadCanvas.
 *
 * Desktop: slides in from the right (~420px wide).
 * Mobile: bottom-sheet (~70vh) for thumb reach.
 *
 * Patterned after CheatSheetDrawer:
 *  - Focus trap
 *  - Escape closes
 *  - Body scroll lock while open
 *  - Backdrop click closes
 *
 * Design choice: scratchpad lives SEPARATE from the spiekbriefje. The
 * spiekbriefje is "help" (and counts toward helpUsed mastery dampening);
 * the scratchpad is "tooling" and has zero mastery impact.
 *
 * The current question prompt is mirrored at the top of the panel so the
 * learner doesn't have to mentally swap between the question and their
 * scratch work — a request from real-classroom UX feedback.
 */
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { ScratchpadCanvas } from './ScratchpadCanvas';
import { useFocusTrap } from '../../app/hooks/useFocusTrap';
import { useEscapeKey } from '../../app/hooks/useEscapeKey';
import { useBodyScrollLock } from '../../app/hooks/useBodyScrollLock';
interface ScratchpadDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Reset signal forwarded to the canvas — typically the current question
   * id so a fresh question starts on a clean sheet.
   */
  resetKey?: string | number;
  /**
   * Optional question prompt mirrored at the top of the panel so the
   * learner can read the question while they work things out on the pad.
   * We deliberately only show the stem, not options/visuals — the goal is
   * orientation, not duplicating the full question UI.
   */
  questionText?: string;
}
export function ScratchpadDrawer({
  isOpen,
  onClose,
  resetKey,
  questionText
}: ScratchpadDrawerProps) {
  const focusRef = useFocusTrap(isOpen);
  useEscapeKey(onClose, isOpen);
  useBodyScrollLock(isOpen);
  return <AnimatePresence data-id="element-11440">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div key="scratchpad-backdrop" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[60] bg-slate-900/30 backdrop-blur-[2px]" onClick={onClose} aria-hidden="true" data-id="element-11441" />

          {/* Drawer — right-side on desktop, bottom-sheet on mobile */}
          <motion.div key="scratchpad-drawer" ref={focusRef} role="dialog" aria-modal="true" aria-label="Kladpapiertje" initial={{
        x: '100%',
        y: 0
      }} animate={{
        x: 0,
        y: 0
      }} exit={{
        x: '100%',
        y: 0
      }} transition={{
        type: 'spring',
        stiffness: 280,
        damping: 32
      }} className="
              fixed z-[61] bg-white shadow-2xl flex flex-col
              right-0 top-0 h-full w-full sm:w-[460px] md:w-[500px]
              border-l border-amber-200
            " data-id="element-11442">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white" data-id="element-11443">
              <div className="flex items-center gap-2" data-id="element-11444">
                <span className="text-base font-black text-slate-800" data-id="element-11445">
                  Kladpapiertje
                </span>
                <span className="hidden sm:inline text-xs text-slate-500" data-id="element-11446">
                  · alleen voor jou
                </span>
              </div>
              <button onClick={onClose} className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors" aria-label="Kladpapiertje sluiten" data-id="element-11447">
                <X className="w-5 h-5" data-id="element-11448" />
              </button>
            </div>

            {/* Question mirror — compact, read-only reminder of the prompt.
                            Kept visually distinct from the scratch surface (white
                            card, slate text) so it doesn't compete with the amber
                            paper below. */}
            {questionText && <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/80" data-id="element-11449">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1" data-id="element-11450">
                  De vraag
                </div>
                <p className="text-sm text-slate-800 leading-snug line-clamp-3" data-id="element-11451">
                  {questionText}
                </p>
              </div>}

            {/* Body */}
            <div className="flex-1 min-h-0" data-id="element-11452">
              <ScratchpadCanvas resetKey={resetKey} data-id="element-11453" />
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}